<template>
    <section
        :id="id"
        :class="[
            embedded ? 'bg-transparent py-0' : 'bg-white px-5 py-10 dark:bg-surface-card lg:px-20',
            { 'border-y border-gray-200 dark:border-default': framed && !embedded }
        ]"
    >
        <div :class="embedded ? '' : 'container mx-auto'">
            <div class="mb-6 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                <div class="max-w-3xl">
                    <p class="mb-2 text-sm font-semibold uppercase tracking-wide text-orange-600 dark:text-orange-400">
                        Pre ľudí z organizácií
                    </p>
                    <h2 class="text-3xl font-semibold text-gray-900 dark:text-white">
                        Príležitosti môžete nielen hľadať, ale aj vytvárať
                    </h2>
                    <p class="mt-3 text-base leading-7 text-gray-600 dark:text-gray-300">
                        Ak prichádzate za firmu, neziskovku, školu alebo verejnú inštitúciu, GROWNi slúži aj ako miesto
                        na získanie ľudí, zdieľanie know-how a budovanie dôveryhodného profilu organizácie.
                    </p>
                </div>

                <NuxtLink
                    to="/pre-organizacie"
                    class="inline-flex w-fit items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-semibold text-gray-900 transition-colors hover:bg-gray-50 dark:border-default dark:bg-surface-card dark:text-white dark:hover:bg-surface-card"
                >
                    <span>Prehľad pre organizácie</span>
                    <Icon name="heroicons:arrow-right" class="size-4" />
                </NuxtLink>
            </div>

            <div class="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <NuxtLink
                    v-for="action in actions"
                    :key="action.title"
                    :to="action.to"
                    class="group flex min-h-[220px] flex-col rounded-lg border border-gray-200 bg-gray-50 p-5 transition-colors hover:border-gray-300 hover:bg-white dark:border-default dark:bg-surface-card dark:hover:border-default dark:hover:bg-surface-card"
                >
                    <div class="mb-5 flex size-11 items-center justify-center rounded-lg" :class="action.iconClass">
                        <Icon :name="action.icon" class="size-6" />
                    </div>

                    <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
                        {{ action.title }}
                    </h3>
                    <p class="mt-2 flex-1 text-sm leading-6 text-gray-600 dark:text-gray-300">
                        {{ action.description }}
                    </p>

                    <span class="mt-5 inline-flex items-center gap-2 text-sm font-semibold" :class="action.linkClass">
                        {{ action.cta }}
                        <Icon
                            name="heroicons:arrow-right"
                            class="size-4 transition-transform group-hover:translate-x-0.5"
                        />
                    </span>
                </NuxtLink>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router'

const props = withDefaults(
    defineProps<{
        id?: string
        orgId?: string
        framed?: boolean
        embedded?: boolean
    }>(),
    {
        id: 'pre-organizacie-actions',
        orgId: '',
        framed: false,
        embedded: false
    }
)

type OrganizationAction = {
    title: string
    description: string
    cta: string
    icon: string
    iconClass: string
    linkClass: string
    to: RouteLocationRaw
}

const routeWithOptionalOrg = (name: string): RouteLocationRaw => {
    if (!props.orgId) {
        return { name }
    }

    return {
        name,
        query: {
            org: props.orgId
        }
    }
}

const actions = computed<OrganizationAction[]>(() => [
    {
        title: 'Zverejniť pracovnú ponuku',
        description: 'Oslovte kandidátov, ktorí hľadajú zmysluplnú prácu, stáž alebo prvú profesionálnu skúsenosť.',
        cta: 'Pridať ponuku',
        icon: 'heroicons:briefcase',
        iconClass: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
        linkClass: 'text-orange-700 dark:text-orange-300',
        to: routeWithOptionalOrg('profile-jobs-create')
    },
    {
        title: 'Ponúknuť mentoring',
        description:
            'Zviditeľnite expertov z organizácie ako mentorov a odovzdávajte skúsenosti ľuďom, ktorí chcú rásť.',
        cta: 'Vytvoriť mentorský profil',
        icon: 'heroicons:chat-bubble-left-right',
        iconClass: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
        linkClass: 'text-purple-700 dark:text-purple-300',
        to: { name: 'profile-mentoring-mentor' }
    },
    {
        title: 'Nájsť dobrovoľníkov',
        description:
            'Pridajte projekt a získajte ľudí, ktorí chcú pomôcť odbornou prácou, časom alebo komunitnou energiou.',
        cta: 'Pridať projekt',
        icon: 'heroicons:heart',
        iconClass: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
        linkClass: 'text-emerald-700 dark:text-emerald-300',
        to: routeWithOptionalOrg('profile-projects-create')
    },
    {
        title: props.orgId ? 'Vyladiť profil organizácie' : 'Vytvoriť profil organizácie',
        description:
            'Ukážte, kto ste, aké hodnoty nesiete a sústreďte všetky svoje príležitosti na jednom verejnom mieste.',
        cta: props.orgId ? 'Upraviť profil' : 'Založiť profil',
        icon: 'heroicons:building-office-2',
        iconClass: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300',
        linkClass: 'text-sky-700 dark:text-sky-300',
        to: props.orgId
            ? { name: 'profile-orgs-edit-id', params: { id: props.orgId } }
            : { name: 'profile-orgs-create' }
    }
])
</script>
